import { SID_ERROR_MSG_NOT_AUTHENTICATED, SID_ERROR_MSG_UNAUTHORIZED } from "@sid/sid_client";
import { SiteLinkage, UserBase } from "@/commons/interfaces";
import { useStore } from "@/store";
import { computed } from "vue";
import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { string } from "yup";
/**
 * 認証エラー判定
 * @param error エラーオブジェクト
 */
export function isAuthFailed(error: Error) {

    if (error.message === SID_ERROR_MSG_NOT_AUTHENTICATED || error.message === SID_ERROR_MSG_UNAUTHORIZED) {
        return true;
    }
    return false;
}

/**
 * サイト登録有無判定
 */
export function isSiteRegistered(linkageId: number, userBase: UserBase | null) {
    return userBase?.siteInfo?.filter((site: any) => site.enable && site.siteLinkageId === linkageId).length;
}

/**
 * ユーザのサイト登録状況取得
 * @param siteLinkage サイト連携情報リスト
 * @param userInfo ユーザ
 */
export function getSiteRegistration (siteLinkage: SiteLinkage[], userInfo: UserBase) {
    // 登録判定
    const isRegister = (linkId: number | null) => (userInfo.siteInfo || []).filter(userSite => userSite.enable && userSite.siteLinkageId == linkId).length > 0;
    // 登録サイト一覧
    const registeredSiteList = computed(() => siteLinkage.filter(s => isRegister(s.link_id) && s.is_disp !== false));
    // 未登録サイト一覧
    const notRegisteredSiteList = computed(() => siteLinkage.filter(s => !isRegister(s.link_id) && s.is_disp !== false));

    return {
        registeredSiteList,
        notRegisteredSiteList
    };
}

/**
 * サービス名取得
 * @param linkageId 連携ID
 */
export function getServiceName(linkageId: number) {
    const store = useStore();
    return store.state.siteLinkage.find((site: any) => site.link_id === linkageId)?.name;
}

/**
 * URIコンポーネント再エンコード
 * @param str コンポーネント文字列
 */
export function reEncodeUriComponent(str: string) {
    return encodeURIComponent(decodeURIComponent(str));
}

/**
 * string型からboolean型へ型変換
 */
export function toBoolean(booleanStr: string | null): boolean {
    if (booleanStr === null)
        return false;
    // "true"文字列と比較した結果を返す
    // 念のため小文字化しておく
    return booleanStr.toLowerCase() === "true";
}

/**
 * 電話番号をE.164形式に
 */
export function convertAuthPhone(authPhone: string,country: CountryCode): string {
    // 文字列が空なら、リターン
    if (!authPhone) {
        return "";
    }

    const phoneNumber = parsePhoneNumberFromString(authPhone, country);

    // 有効な番号かチェック
    if (phoneNumber && phoneNumber.isValid()) {       
        // 成功した場合: E.164形式の文字列を返す
        return phoneNumber.format("E.164");
    }

    // 失敗した場合: 空の文字列を返す
    return "";
}

/**
 * 電話番号を解析(E.164形式 → 国内番号)
 */
export function convertAuthPhoneNational(e164Number: string,country: CountryCode): string {
    // 文字列が空なら、リターン
    if (!e164Number) {
        return "";
    }
    const phoneNumber = parsePhoneNumberFromString(e164Number, country);

    // 有効な電話番号かを確認
    if (phoneNumber && phoneNumber.isValid()) {
        // ハイフンなしの形式（例: "09012345678"）
        return phoneNumber.format("NATIONAL").replace(/[-\s]/g, "");
    }

    // 失敗
    return "";
}

/**
 * 電話番号をマスクする
 * @param phoneNumber 電話番号(国内形式)
 */
export function maskPhoneNumber(phoneNumber: string) {
    if (!phoneNumber) return "";
    // 形式が異なる場合はマスクしない
    if (!/^(\d{3})(\d{4})(\d{4})$/.test(phoneNumber)) return phoneNumber; // 10桁または11桁の数字以外はマスクしない
    // 真ん中4桁をマスク 090****5678
    return phoneNumber.replace(/^(\d{3})(\d{4})(\d{4})$/, "$1****$3");
}
